<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
import { useSurveyStore } from "@stores/survey/survey";
import { usePopulationLabels } from "@/composables/usePopulationLabels";
import { languageDirection } from "@/utils/textDirection";
import { populationColor, POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";

const store = useResultsV2Store();
const populationsStore = useResultsV2PopulationsStore();
const { viewName, baseRespondents, printing, effectiveShowAllPopulation } = storeToRefs(store);
const { populations } = storeToRefs(populationsStore);
const { survey } = storeToRefs(useSurveyStore());
const { filterSummary } = usePopulationLabels();

const textDirection = computed(() => languageDirection(store.displayLanguage));

const printedPopulations = computed(() =>
  populations.value.map((pop, index) => ({
    key: pop.id || index,
    name: `${pop.search_group?.name || "Population"}${populationsStore.isNarrowed(pop) ? "*" : ""}`,
    color: populationColor(pop.search_group?.token, populations.value),
    filters: filterSummary(pop.search_group?.filters).split("\n").join(" · "),
  })),
);
</script>

<template>
  <div class="px-1 pt-1 pb-6">
    <h1 :dir="textDirection" class="heading-page">{{ viewName }}</h1>
    <p class="mt-1 text-xs text-muted-foreground">
      {{ survey?.name }}<span v-if="baseRespondents"> · n={{ baseRespondents }}</span>
    </p>

    <div v-if="printing" class="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
      <span v-if="effectiveShowAllPopulation" class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: POPULATION_AUTO_COLORS[0] }" />
        All respondents
      </span>
      <span v-for="pop in printedPopulations" :key="pop.key" class="flex items-center gap-1.5">
        <span class="h-2 w-2 shrink-0 rounded-full" :style="{ backgroundColor: pop.color }" />
        <span class="font-medium text-foreground">{{ pop.name }}</span>
        <span>{{ pop.filters }}</span>
      </span>
    </div>
  </div>
</template>
